import axios from 'axios';
import { getToken, saveToken } from '../utils/tokenHelper';
import { useAuthStore } from '../store/authStore';

// API URL from environment variables
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api/v1";

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
// Store for pending requests that should be retried after token refresh
let refreshSubscribers: ((token: string) => void)[] = [];

// Function to add callbacks to pending requests queue
const subscribeTokenRefresh = (callback: (token: string) => void): void => {
  refreshSubscribers.push(callback);
};

// Function to execute all pending requests with new token
const onTokenRefreshed = (token: string): void => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Function to reject all pending requests
const onRefreshError = (error: Error): void => {
  refreshSubscribers.forEach((callback) => callback(''));
  refreshSubscribers = [];
};

// Create an axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15 seconds
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Request interceptor to add auth token to all requests
axiosInstance.interceptors.request.use((config) => {
  // Get token from helper
  const token = getToken();
  
  // If token exists, add it to the headers
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn(`No token available for request to: ${config.url}`);
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor to handle common errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle authentication errors (401)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Mark that we've attempted to retry this request
      originalRequest._retry = true;
      
      // If already refreshing token, add request to queue
      if (isRefreshing) {
        try {
          // Wait for new token or error
          const newToken = await new Promise<string>((resolve, reject) => {
            subscribeTokenRefresh((token) => {
              if (token) resolve(token);
              else reject(new Error("No token received"));
            });
          });
          
          // If we got a new token, retry the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshSubError) {
          // If subscriber process failed, redirect to login
          useAuthStore.getState().logout(false); // Don't navigate, just clear state
          window.location.href = "/auth/login"; // Direct redirect
          return Promise.reject(refreshSubError);
        }
      }
      
      // Start the refresh process
      isRefreshing = true;
      
      try {
        // Call the refresh token endpoint
        const response = await axios.get(
          `${API_URL}/auth/refreshToken`,
          { withCredentials: true } // Include cookies in this request
        );
        
        // If refresh successful, update token and retry
        if (response.data && response.data.token) {
          const newToken = response.data.token;
          
          // Save token to localStorage and sessionStorage
          saveToken(newToken);
          
          // Update token in auth store
          useAuthStore.getState().setToken(newToken);
          
          // Update authorization in original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Process any pending requests
          onTokenRefreshed(newToken);
          
          // Reset the refreshing flag
          isRefreshing = false;
          
          // Retry the original request with new token
          return axiosInstance(originalRequest);
        } else {
          throw new Error("Failed to refresh token");
        }
      } catch (refreshError) {
        // If refresh failed, clear auth state and redirect to login
        isRefreshing = false;
        onRefreshError(refreshError instanceof Error ? refreshError : new Error("Unknown refresh token error"));
        useAuthStore.getState().logout(false); // Don't navigate, just clear state
        window.location.href = "/auth/login"; // Direct redirect
        return Promise.reject(refreshError);
      }
    }
    
    // Handle server errors (500) and network errors
    if (error.response && error.response.status >= 500) {
      console.error('Server error:', error.response.data);
    }
    
    // Handle network errors (server offline, no internet, etc.)
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      console.error('Network error - server may be offline:', error.message);
      
      // If this is an authenticated request and server is offline,
      // only redirect to login for dashboard-related requests
      if (originalRequest.headers.Authorization) {
        // Check if this is a dashboard-related request by looking at the URL path
        const requestUrl = originalRequest.url || '';
        const isDashboardRequest = requestUrl.includes('/dashboard') || 
                                  requestUrl.includes('/orders') ||
                                  requestUrl.includes('/profile') ||
                                  requestUrl.includes('/settings') ||
                                  requestUrl.includes('/users') ||
                                  requestUrl.includes('/products') ||
                                  requestUrl.includes('/enclosure') ||
                                  requestUrl.includes('/custom-quote') ||
                                  requestUrl.includes('/folder') ||
                                  requestUrl.includes('/savedcart') ||
                                  requestUrl.includes('/quote-form') ||
                                  requestUrl.includes('/admin');
        
        if (isDashboardRequest) {
          console.warn('Server offline during dashboard request - redirecting to login');
          
          // Clear auth state first so PrivateRoute doesn't interfere
          useAuthStore.getState().logout(false);
          
          // Force direct redirect to login
          window.location.replace("/auth/login");
          return Promise.reject(error);
        } else {
          // For non-dashboard requests, just log the error but don't redirect
          console.warn('Server offline during non-dashboard request - allowing to continue');
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance; 